import type { ActionReducerMapBuilder } from '@reduxjs/toolkit';

import { RedactedSupplierIntegrationDetails } from '@dill/dill-shared';
import { AppServerResponse } from '../../utils/interfaces/generalInterfaces';
import { SupplierIntegrationDetailsInitialState } from '../globalSlices/supplierIntegrationDetailsSlice';
import { getSupplierIntegrationDetails } from '../services/supplierAdditionalDetailsService';

export const getSupplierIntegrationDetailsExtraReducer = (
  builder: ActionReducerMapBuilder<SupplierIntegrationDetailsInitialState>
) => {
  const reducerId = 'getSupplierIntegrationDetails';
  builder.addCase(getSupplierIntegrationDetails.pending, (state, action) => {
    state.loadingList = [...new Set([...state.loadingList, reducerId])];
    // This state used to ride along on `currentSupplier`, so a supplier switch replaced it
    // atomically. Now it is a separate request, so drop the outgoing supplier's details up front -
    // otherwise the dashboard renders the previous supplier's connection state until this lands.
    // Keyed on supplierId so a re-fetch for the supplier already in state doesn't blank the UI.
    if (state.supplierId !== action.meta.arg.supplierId) {
      state.integrationDetails = null;
    }
    state.supplierId = action.meta.arg.supplierId;
    state.error = null;
  });
  builder.addCase(getSupplierIntegrationDetails.fulfilled, (state, action) => {
    state.loadingList = state.loadingList.filter((item) => item !== reducerId);
    const payload = action.payload as AppServerResponse | null;
    if (payload && payload.success && payload?.data) {
      state.integrationDetails = payload.data as RedactedSupplierIntegrationDetails;
    }
  });
  builder.addCase(getSupplierIntegrationDetails.rejected, (state, action) => {
    state.loadingList = state.loadingList.filter((item) => item !== reducerId);
    // Left as-is rather than cleared: a transient failure shouldn't drop details that are still
    // correct for this supplier. Recording the error keeps the staleness from being silent.
    state.error = (action.payload as string) ?? action.error?.message ?? 'Unknown error';
  });
};
